import React from 'react';
import { ExamProps } from '../../FormRisk.types';
import { getExamSynonyms } from 'Views/JobProfile/services/services';
import { useToast } from '@welbe/web-design-system';

type AutocompleteProps = {
	onAddExam: (exam: ExamProps) => void;
};

export const useAutocomplete = ({ onAddExam }: AutocompleteProps) => {
	const { toast } = useToast();
	const debounceRef = React.useRef<any>(null);
	const [preventRender, setPreventRender] = React.useState(false);
	const [optionsVisible, setOptionsVisible] = React.useState(false);
	const [suggestions, setSuggestion] = React.useState([] as ExamProps[]);
	const [loading, setLoading] = React.useState(false);
	const [searchString, setSearchString] = React.useState('');
	const [selected, setSelected] = React.useState<ExamProps>({} as ExamProps);

	const handleSelect = (item: ExamProps) => {
		setPreventRender(true);
		setOptionsVisible(false);
		setSearchString(item.name);
		setSelected(item);
	};

	const handleFocus = () => {
		if (searchString.length) {
			setOptionsVisible(true);
		}
	};

	const handleAddExam = () => {
		onAddExam(selected);
		setSearchString('');
		setSelected({} as ExamProps);
	};

	const debouncedSearch = async () => {
		clearTimeout(debounceRef.current);

		if (preventRender) {
			setPreventRender(false);
			return;
		}

		try {
			setLoading(true);
			const { data } = await getExamSynonyms(searchString);
			setSuggestion(data.content);
			setOptionsVisible(true);
		} catch (error) {
			toast.error('Error al buscar los estudios');
		} finally {
			setLoading(false);
		}
	};

	React.useEffect(() => {
		const handleClick = (e: any) => {
			if (e.target.dataset.testid !== 'search-exam') {
				setOptionsVisible(false);
			}
		};

		document.addEventListener('click', handleClick);

		return () => {
			document.removeEventListener('click', handleClick);
		};
	}, []);

	React.useEffect(() => {
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			searchString.length > 2 && debouncedSearch();
		}, 1500);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchString]);

	return {
		loading,
		selected,
		suggestions,
		searchString,
		optionsVisible,
		handleSelect,
		handleFocus,
		handleAddExam,
		setSearchString,
	};
};
